import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Grid2, Stack, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { AlertModalProfileImage } from "../../../assets";
import ProfileBaseFavicon from "../../../assets/images/profilebasefavicon.svg";
import { AppLayout } from "../../../containers/layout/app";
import { ProfileWrapper } from "./styled";
import { BaseButton } from "../../../components/button/styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { updateUserProfileService } from "../../../utils/api/usermanagement/profile/updateUser";
import { BaseAlertModal } from "../../../components/modal/alert";

export const Profile = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    interface ProfileFormDetailsType {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        avatar: File | string;
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState<ProfileFormDetailsType>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        avatar: "",
    });
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (TOKEN) {
                const response = await retrieveUserProfileService(TOKEN);
                const { firstName, lastName, email, phone, avatar } = response;
                setFormDetails({ firstName, lastName, email, phone, avatar });
            }
        };
        fetchUserProfile();
    }, [TOKEN]);

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        return setIsAlertModalOpen(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUploadClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation()
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        setImagePreview("");
        setFormDetails((prev) => ({
            ...prev,
            avatar: ""
        }));
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target?.result) {
                    setImagePreview(e.target.result as string);
                    setFormDetails((prev) => ({
                        ...prev,
                        avatar: file
                    }))
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        const formData = new FormData();
        const data = {
            avatar: formDetails.avatar,
            firstName: formDetails.firstName,
            lastName: formDetails.lastName,
            email: formDetails.email,
            phone: formDetails.phone,
        };
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });
        try {
            const response = await updateUserProfileService(TOKEN, formData);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to update profile. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update profile. ${error.message}`);
            console.error('Failed to update profile:', error);
        }
    }

    return (
        <AppLayout
            pageId={"User-Profile"}
            pageTitle={"User Profile"}
        >
            <ProfileWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalProfileImage />}
                    title="Profile Successfully Updated"
                    message="Your profile has been successfully updated"
                    callToAction="Close"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required Information to update profile.
                    </Typography>
                </Box>
                <Stack
                    direction={{ desktop: "row" }}
                    gap={"var(--flex-gap)"}
                    alignItems={{ desktop: "center" }}
                >
                    <Stack
                        gap={"calc(var(--flex-gap)/4)"}
                    >
                        <Box>
                            <Typography
                                variant="h2"
                                fontFamily={"Poppins"}
                                fontWeight={500}
                                fontSize={16}
                                lineHeight={"normal"}
                                color="var(--dark-color)"
                            >
                                Profile Picture
                            </Typography>
                        </Box>
                        <Box
                            component={"div"}
                            className="profile-base-favicon-box"
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Profile Preview"
                                    className="client-avatar"
                                />
                            ) : (
                                <img
                                    src={formDetails?.avatar as string || ProfileBaseFavicon}
                                    alt="Headshot"
                                    className="client-avatar"
                                />
                            )}
                        </Box>
                    </Stack>
                    <Stack
                        gap={{ mobile: "calc(var(--flex-gap)/2)", laptop: "var(--flex-gap)", desktop: "calc(var(--flex-gap)/2)" }}
                        direction={{ miniTablet: "row" }}
                    >
                        <Box
                            flex={{ mobile: 1, desktop: "0 1 auto" }}
                            overflow={"hidden"}
                        >
                            <BaseInput
                                as={"input"}
                                type="file"
                                name="avatar"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                inputProps={{ accept: "image/jpeg, image/png" }}
                                onChange={handleFileChange}
                            />
                            <BaseButton
                                variant="contained"
                                bgcolor="var(--sky-blue-color)"
                                fullWidth
                                disableElevation
                                onClick={(e) => handleUploadClick(e)}
                            >
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Change Picture
                                </Typography>
                            </BaseButton>
                        </Box>
                        <Box
                            flex={{ mobile: 1, desktop: "0 1 auto" }}
                            overflow={"hidden"}
                        >
                            <BaseButton
                                variant="contained"
                                bgcolor="var(--inactive-grey-color)"
                                colour="var(--text-crimson-ember-color)"
                                fullWidth
                                disableElevation
                                onClick={(e) => handleDeleteClick(e)}
                            >
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Delete Picture
                                </Typography>
                            </BaseButton>
                        </Box>
                    </Stack>
                </Stack>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    First Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="firstName"
                                    value={formDetails.firstName}
                                    placeholder="First Name"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Last Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="lastName"
                                    value={formDetails.lastName}
                                    placeholder="Last Name"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Email
                                </BaseLabel>
                                <BaseInput
                                    required
                                    readOnly
                                    name="email"
                                    value={formDetails.email}
                                    placeholder="Email"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Phone Number
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="phone"
                                    value={formDetails.phone}
                                    placeholder="Phone Number"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Update Profile
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </ProfileWrapper>
        </AppLayout>
    )
}